import type { App } from 'vue'
import { useEventBus } from '@vueuse/core'

export const AwaitAsyncTasksSymbol = Symbol('AwaitAsyncTasks')

export type Task = (taskResults: Record<string, TaskResult>) => Promise<unknown>

export interface AwaitAsyncTasksServiceMethods {
  add: (name: string, task: Task) => void

  remove: (task: Task | string) => void

  run: (header: string | undefined, callback: (results: TaskResults) => void) => void

  clear: () => void

  runAsync: (header?: string) => Promise<TaskResults>
}

export interface TaskResults {
  started: Date
  ended: Date
  duration: number
  results: Record<string, TaskResult>
  success: boolean
}

export interface TaskResult {
  started?: Date
  ended?: Date
  duration?: number
  result?: unknown
  error?: Error
}

export enum AwaitAsyncTasksTaskState {
  ADDED = 'ADDED',
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED'
}

export interface AwaitAsyncTasksAddEvent {
  name: string
  task: Task
}

export interface AwaitAsyncTasksRemoveEvent {
  name?: string
  task?: Task
}

export default {
  install: (app: App) => {
    const AwaitAsyncTasksService: AwaitAsyncTasksServiceMethods = {
      async runAsync(header?: string): Promise<TaskResults> {
        return new Promise<TaskResults>((e) => {
          this.run(header, e)
        })
      },
      clear(): void {
        useEventBus('awaitasynctasks.clear').emit()
      },

      add: (name: string, task: Task) => {
        const event: AwaitAsyncTasksAddEvent = {
          name: name,
          task: task
        }
        useEventBus<AwaitAsyncTasksAddEvent>('awaitasynctasks.add').emit(event)
      },
      remove: (taskOrName: Task | string): void => {
        const event: AwaitAsyncTasksRemoveEvent = {}
        if (typeof taskOrName === 'string') event.name = taskOrName
        else event.task = taskOrName
        useEventBus<AwaitAsyncTasksRemoveEvent>('awaitasynctasks.remove').emit()
      },
      run: (header: string | undefined, callback: (results: TaskResults) => void): void => {
        useEventBus<TaskResults>('awaitasynctasks.finish').once(callback)
        useEventBus<string>('awaitasynctasks.run').emit(header)
      }
    }
    app.config.globalProperties.$awaitasynctasks = AwaitAsyncTasksService
    app.provide(AwaitAsyncTasksSymbol, AwaitAsyncTasksService)
  }
}
